import { useEffect, useMemo, useState } from "react";
import { Activity, Clock, MessageSquare, FileWarning, User, UserCog, GitBranch, PauseCircle, CalendarDays, LogOut, FileText, Coins, MinusCircle, Wallet, Banknote, ClipboardList, PiggyBank, Timer, ArrowLeftRight } from "lucide-react";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { useNavigate } from "react-router-dom";

import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import { EMPLOYEE_ACTIVITY } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";
import EmployeeRemarksContent from "./EmployeeRemarksContent";

const TYPE_STYLES = {
    remark: {
        icon: MessageSquare,
        chip: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
    },
    attendance_correction: {
        icon: Clock,
        chip: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    },
    correction_request: {
        icon: FileWarning,
        chip: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    },
    profile_change: {
        icon: UserCog,
        chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    },
    branch_transfer: {
        icon: GitBranch,
        chip: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
    },
    payroll_hold: {
        icon: PauseCircle,
        chip: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    },
    status: {
        icon: UserCog,
        chip: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
    },
    leave: {
        icon: CalendarDays,
        chip: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
    },
    resignation: {
        icon: LogOut,
        chip: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
    },
    reimbursement: {
        icon: Wallet,
        chip: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    },
    advance_salary: {
        icon: Banknote,
        chip: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
    },
    document: {
        icon: FileText,
        chip: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    },
    allowance: {
        icon: Coins,
        chip: "bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300",
    },
    deduction: {
        icon: MinusCircle,
        chip: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300",
    },
    timesheet: {
        icon: ClipboardList,
        chip: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300",
    },
    deposit: {
        icon: PiggyBank,
        chip: "bg-stone-100 text-stone-700 dark:bg-stone-900/30 dark:text-stone-300",
    },
    overtime: {
        icon: Timer,
        chip: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
    },
    shift_change_request: {
        icon: ArrowLeftRight,
        chip: "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300",
    },
    shift_change: {
        icon: ArrowLeftRight,
        chip: "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300",
    },
};

const PROFILE_ACTIVITY_TYPES = [
    "profile_change", "branch_transfer", "payroll_hold", "status",
    "leave", "resignation", "reimbursement", "advance_salary", "document",
];

const REQUEST_ACTIVITY_TYPES = [
    "leave",
    "resignation",
    "reimbursement",
    "advance_salary",
    "correction_request",
    "overtime",
    "shift_change_request",
];

const formatActor = (item) => {
    const name = item?.actor_name || "Unknown";
    const code = item?.actor_code;
    if (!code || String(name).includes(`(${code})`)) return name;
    return `${name} (${code})`;
};

const EmployeeActivityContent = ({ employeeID, selfView = false }) => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const filterOptions = useMemo(() => [
        { value: "all", label: "All" },
        { value: "remark", label: "Remarks" },
        { value: "attendance_correction", label: "Corrections" },
        { value: "requests", label: "Requests" },
        { value: "timesheet", label: "Tasks" },
        { value: "deposit", label: "Deposits" },
        { value: "profile", label: "Profile changes" },
        { value: "allowance", label: "Allowances" },
        { value: "deduction", label: "Deductions" },
    ], []);

    useEffect(() => {
        if (!employeeID) return;

        const loadActivity = async () => {
            setLoading(true);
            try {
                const filters = { employee_id: employeeID, limit: 200 };
                if (fromDate) filters.from_date = dayjs(fromDate).format("YYYY-MM-DD");
                if (toDate) filters.to_date = dayjs(toDate).format("YYYY-MM-DD");
                const apiData = await fetch(EMPLOYEE_ACTIVITY, filters);
                if (apiData?.status === "success") {
                    setItems(apiData.data || []);
                } else {
                    ToastMsg("error", apiData?.message || "Failed to load activity history");
                }
            } catch (error) {
                ToastMsg("error", error.message);
            } finally {
                setLoading(false);
            }
        };

        loadActivity();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeID, fromDate, toDate]);

    const filteredItems = useMemo(() => {
        if (filter === "all") return items;
        if (filter === "profile") {
            return items.filter((item) => PROFILE_ACTIVITY_TYPES.includes(item.activity_type));
        }
        if (filter === "requests") {
            return items.filter((item) => REQUEST_ACTIVITY_TYPES.includes(item.activity_type));
        }
        return items.filter((item) => item.activity_type === filter);
    }, [items, filter]);

    if (loading && items.length === 0) {
        return (
            <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <Activity className="h-5 w-5 text-sidebar-primary" />
                        Activity History
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        {selfView
                            ? "Your leave, shift changes, requests, tasks, deposits, attendance, document, profile updates, and remarks entered by HR or your manager."
                            : "Remarks, attendance, leave, shift changes, requests, tasks, deposits, profile changes, and variable pay updates."}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <DatePicker
                        value={fromDate}
                        onChange={setFromDate}
                        placeholder="From date"
                        className="w-36"
                        allowClear
                    />
                    <DatePicker
                        value={toDate}
                        onChange={setToDate}
                        placeholder="To date"
                        className="w-36"
                        allowClear
                    />
                    {filterOptions.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => setFilter(option.value)}
                            className={cn(
                                "px-3 py-1.5 rounded-full text-xs font-medium border transition",
                                filter === option.value
                                    ? "bg-sidebar-primary text-white border-sidebar-primary"
                                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700",
                            )}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {filteredItems.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
                    <Activity className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
                </div>
            ) : (
                <div className="max-h-[min(560px,calc(100vh-320px))] overflow-y-auto pr-1 space-y-3">
                    {filteredItems.map((item) => {
                        const style = TYPE_STYLES[item.activity_type] || TYPE_STYLES.remark;
                        const Icon = style.icon;
                        const meta = item.metadata || {};
                        return (
                            <div
                                key={item.id}
                                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 p-4"
                            >
                                <div className="flex items-start gap-3">
                                    <div className={cn("p-2 rounded-lg shrink-0", style.chip)}>
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                            <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", style.chip)}>
                                                {item.activity_label}
                                            </span>
                                            <span className="text-xs text-muted-foreground">{item.occurred_at || "-"}</span>
                                        </div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.title}</p>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-wrap break-words">
                                            {item.description}
                                        </p>
                                        {item.activity_type === "attendance_correction" && (
                                            <div className="mt-2 text-xs text-muted-foreground grid grid-cols-1 sm:grid-cols-2 gap-1">
                                                <span>
                                                    Before: {meta.old_value || meta.old_status || "-"}
                                                    {meta.old_check_in ? ` · In ${dayjs(meta.old_check_in).format("h:mm A")}` : ""}
                                                    {meta.old_check_out ? ` · Out ${dayjs(meta.old_check_out).format("h:mm A")}` : ""}
                                                </span>
                                                <span>
                                                    After: {meta.new_value || meta.new_status || "-"}
                                                    {meta.new_check_in ? ` · In ${dayjs(meta.new_check_in).format("h:mm A")}` : ""}
                                                    {meta.new_check_out ? ` · Out ${dayjs(meta.new_check_out).format("h:mm A")}` : ""}
                                                </span>
                                            </div>
                                        )}
                                        {(PROFILE_ACTIVITY_TYPES.includes(item.activity_type) || item.activity_type === "shift_change" || item.activity_type === "shift_change_request") && (
                                            <div className="mt-2 text-xs text-muted-foreground grid grid-cols-1 sm:grid-cols-2 gap-1">
                                                <span>Before: {meta.old_value || meta.current_shift || "-"}</span>
                                                <span>After: {meta.new_value || meta.requested_shift || "-"}</span>
                                            </div>
                                        )}
                                        {item.activity_type === "timesheet" && (meta.old_value || meta.new_value) && (
                                            <div className="mt-2 text-xs text-muted-foreground grid grid-cols-1 sm:grid-cols-2 gap-1">
                                                <span>Before: {meta.old_value || "-"}</span>
                                                <span>After: {meta.new_value || "-"}</span>
                                            </div>
                                        )}
                                        {item.activity_type === "correction_request" && meta.admin_comment && (
                                            <p className="text-xs text-muted-foreground mt-2">
                                                Admin: {meta.admin_comment}
                                            </p>
                                        )}
                                        {item.activity_type === "shift_change_request" && (
                                            <div className="mt-2 text-xs text-muted-foreground space-y-1">
                                                <p>
                                                    {meta.current_shift || "—"}
                                                    {" → "}
                                                    {meta.requested_shift || "—"}
                                                </p>
                                                {meta.admin_comment && (
                                                    <p>Review note: {meta.admin_comment}</p>
                                                )}
                                                {meta.reviewed_on && (
                                                    <p>Reviewed: {meta.reviewed_on}</p>
                                                )}
                                                {selfView && (
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate("/attendance/my-shift-change-requests")}
                                                        className="text-sidebar-primary hover:underline font-medium"
                                                    >
                                                        View all my shift change / swap requests
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                                            <User className="h-3.5 w-3.5" />
                                            <span>Actor: {formatActor(item)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <EmployeeRemarksContent
                employeeID={employeeID}
                compact
                heading={selfView ? "Remarks about you" : "Remarks"}
                allowAdd={false}
                serverDecidesAccess
            />
        </div>
    );
};

export default EmployeeActivityContent;
